export type MediaAttachmentType = "youtube" | "video" | "pdf";
export type MediaAttachmentLayout = "portrait" | "landscape" | "auto";

export type ContentMediaAttachment = {
  attachmentType: MediaAttachmentType;
  attachmentUrl: string;
  attachmentLayout?: MediaAttachmentLayout;
};

export function parseYoutubeId(url: string): string | null {
  const u = (url || "").trim();
  if (!u) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m?.[1]) return m[1];
  }
  return null;
}

export function parseVimeoId(url: string): string | null {
  const m = (url || "").trim().match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m?.[1] || null;
}

export function isPdfUrl(url: string): boolean {
  const u = (url || "").trim();
  if (/\.pdf(\?|#|$)/i.test(u)) return true;
  return /\/raw\/upload\//i.test(u);
}

export function isDirectVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test((url || "").trim());
}

export function inferMediaType(url: string): MediaAttachmentType | null {
  const u = (url || "").trim();
  if (!u || !/^https?:\/\//i.test(u)) return null;
  if (parseYoutubeId(u) || /youtube\.com|youtu\.be/i.test(u)) return "youtube";
  if (isPdfUrl(u)) return "pdf";
  if (parseVimeoId(u) || isDirectVideoUrl(u)) return "video";
  return "video";
}

export function getContentMediaAttachment(item: {
  attachmentType?: string | null;
  attachmentUrl?: string | null;
  attachmentLayout?: string | null;
  location?: string | null;
  content?: string | null;
}): ContentMediaAttachment | null {
  const type = item.attachmentType;
  const url = (item.attachmentUrl || "").trim();
  if (type && url && ["youtube", "video", "pdf"].includes(type)) {
    const layout = (item.attachmentLayout as MediaAttachmentLayout) || "auto";
    return {
      attachmentType: type as MediaAttachmentType,
      attachmentUrl: url,
      attachmentLayout: layout,
    };
  }
  const legacyUrl =
    item.location && /^https?:\/\//i.test(item.location) ? item.location.trim() : null;
  const pdfFromHtml = (item.content || "").match(/href="(https?:\/\/[^"]+\.pdf[^"]*)"/i)?.[1];
  const fallback = legacyUrl || pdfFromHtml || null;
  if (!fallback) return null;
  const inferred = inferMediaType(fallback) || (isPdfUrl(fallback) ? "pdf" : "video");
  return {
    attachmentType: inferred,
    attachmentUrl: fallback,
    attachmentLayout: "auto",
  };
}

export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
}

export function vimeoEmbedUrl(videoId: string): string {
  return `https://player.vimeo.com/video/${videoId}`;
}
